import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Autocomplete,
  Collapse,
  Dialog,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  IconButton,
  Slider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import theme from "../theme";
import { useDropzone } from "react-dropzone";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { isServiceHead } from "../utils/roles";
import { useBranchCreateGuard } from "./BranchCreateGuard";
import TicketReceipt from "./TicketReceipt";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { STORE_ID } from "../Config/Constants";

// icons
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import LoadingScreen from "./LoadingScreen";
import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HistoryToggleOffRoundedIcon from "@mui/icons-material/HistoryToggleOffRounded";
import EmojiFlagsRoundedIcon from "@mui/icons-material/EmojiFlagsRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import TicketCard from "./TicketCard";
import { whatsAppFailureText } from "../services/wpService";

function RaiseServiceTicket({
  deptId,
  storeId,
  pmTask,
  isDialogMode = false,
  onTicketCreated,
  onClose,
  showSnack: showSnackProp,
}) {
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const contextData = useContext(AutoTaskAuthContext);
  const { allStores, allKumuduDepartments, allInternalDepartments, allUsers } =
    contextData;
  const userInfo = contextData?.userInfo;
  const userStoreID = userInfo?.store_id;
  const { branchScope, currentStoreInfo } = contextData || {};

  // Nothing may be raised while every branch is on screen at once — the ticket
  // has to record one branch, and there is no honest way to choose from there.
  // A pass-through for everyone else.
  const { guard: guardBranch, guardDialog: branchGuardDialog } =
    useBranchCreateGuard({
      reason:
        "You are viewing every branch. Pick the branch this ticket is being " +
        "raised at before saving it.",
    });

  // Stores that can actually be picked.
  //
  // store_master.name is nullable and the stores API returns it as-is, so a
  // half-created store arrives here with store_name: null. Two things went
  // wrong with that. filterOptions called .toLowerCase() on it and threw,
  // which killed the whole picker rather than one row — the filter runs across
  // every option on each keystroke. And getOptionLabel returns "" for it, so
  // it rendered as a blank but clickable row: selecting it raised a ticket
  // against a store with no name.
  //
  // A store nobody can identify is not a choice, so it is not offered. Dropped
  // here rather than guarded at each use, because every later reader of this
  // list would otherwise have to remember the same thing.
  const selectableStores = useMemo(
    () => (allStores || []).filter((s) => String(s?.store_name ?? "").trim()),
    [allStores],
  );
  const user_id = userInfo?.id;
  const allStaff = allUsers;
  // userStoreID === 0
  //   ? allKumuduDepartments?.map((dept) => allStaff.push(...dept?.staff))
  //   : allInternalDepartments?.map((dept) => allStaff.push(...dept?.staff));
  // AFTER
  const location = useLocation();
  const selectedServiceDept = location?.state;

  // ✅ In dialog mode use props, otherwise use route state
  const dept_id = isDialogMode ? deptId : selectedServiceDept?.service?.dept_id;
  const store_id = isDialogMode
    ? storeId
    : selectedServiceDept?.service?.store_id;
  const selectedDeptStoreId = store_id;
  const serviceDescriptionTotalChar = 1000;
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const [isFocused, setIsFocused] = useState(false);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [overlayFile, setOverlayFile] = useState(null);
  const [serviceDescriptionCharCount, setServiceDescriptionCharCount] =
    useState(0);
  const autoDesc = pmTask
    ? `${pmTask.screen_name} - ${pmTask.pm_id}${
        pmTask.description ? ` | ${pmTask.description}` : ""
      }`
    : "";

  const [serviceTicketDetails, setServiceTicketDetails] = useState({
    serviceDescription: autoDesc,
    serviceAudioDescription: "",
    serviceImages: [],
    serviceStartDate: formatDate(today),
    serviceDeadline: formatDate(tomorrow),
    servicePriorityLevel: "medium",
    serviceIsRecurring: "",
    ServiceTicketFrequency: "",
    serviceFrequencyValue: "",
  });

  const [isAssignNow, setIsAssignNow] = useState(
    selectedDeptStoreId === userStoreID ? true : false,
  );
  const [assignForm, setAssignForm] = useState({
    ticket_id: "",
    assigned_to:
      isDialogMode && pmTask?.owner_id ? pmTask.owner_id : user_id || "",
    description: "",
    time_budget: isDialogMode && pmTask?.time_budget ? pmTask.time_budget : 0,
    time_taken: "",
    status: "in_progress",
    media: "",
  });
  const [storeForm, setStoreForm] = useState({
    store_name: "",
    store_id: "",
    head_id: "",
  });
  const [allTickets, setAllTickets] = useState(null);

  const priorityOptions = [
    {
      label: "Low",
      value: "low",
      icon: (
        <EmojiFlagsRoundedIcon
          sx={{ fill: theme.lightTheme.prioritieIndicators.low }}
        />
      ),
    },
    {
      label: "Medium",
      value: "medium",
      icon: (
        <EmojiFlagsRoundedIcon
          sx={{ fill: theme.lightTheme.prioritieIndicators.medium }}
        />
      ),
    },
    {
      label: "High",
      value: "high",
      icon: (
        <EmojiFlagsRoundedIcon
          sx={{ fill: theme.lightTheme.prioritieIndicators.high }}
        />
      ),
    },
  ];
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return theme.lightTheme.prioritieIndicators.low;
      case "medium":
        return theme.lightTheme.prioritieIndicators.medium;
      case "high":
        return theme.lightTheme.prioritieIndicators.high;
      default:
        return "#000000";
    }
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "in_progress":
        return theme.lightTheme.statuses.in_progress.textCol;
      case "resolved":
        return theme.lightTheme.statuses.resolved.textCol;
      case "follow_up":
        return theme.lightTheme.statuses.follow_up.textCol;
      case "recurring":
        return theme.lightTheme.statuses.recurring.textCol;
      default:
        return "#000000";
    }
  };
  const getStatusBorderColor = (status) => {
    switch (status) {
      case "in_progress":
        return theme.lightTheme.statuses.in_progress.borderCol;
      case "resolved":
        return theme.lightTheme.statuses.resolved.borderCol;
      case "follow_up":
        return theme.lightTheme.statuses.follow_up.borderCol;
      case "recurring":
        return theme.lightTheme.statuses.recurring.borderCol;
      default:
        return "#000000";
    }
  };
  const getStatusBgColor = (status) => {
    switch (status) {
      case "in_progress":
        return theme.lightTheme.statuses.in_progress.bgCol;
      case "resolved":
        return theme.lightTheme.statuses.resolved.bgCol;
      case "follow_up":
        return theme.lightTheme.statuses.follow_up.bgCol;
      case "recurring":
        return theme.lightTheme.statuses.recurring.bgCol;
      default:
        return "#000000";
    }
  };

  // Ticket overlay after successful ticket creation
  const [ticket, setTicket] = useState(null);
  const [assignRes, setAssignRes] = useState(null);
  const [overlayOpen, setOverlayOpen] = useState(false);

  // Lock assignee to the logged-in user when status is follow_up
  useEffect(() => {
    if (assignForm.status === "follow_up") {
      setAssignForm((prev) => ({
        ...prev,
        assigned_to: user_id,
      }));
    }
  }, [assignForm.status, user_id]);

  // ------------------ FOR AUDIO RECORD FEATURE ------------------
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const [audioURL, setAudioURL] = useState("");

  const [isRecording, setIsRecording] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);

  const audioRef = useRef(null);

  const handleMicClick = async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        let mimeType = "";
        if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
        } else if (MediaRecorder.isTypeSupported("audio/mpeg")) {
          mimeType = "audio/mpeg";
        } else if (MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")) {
          mimeType = "audio/ogg; codecs=opus";
        } else if (MediaRecorder.isTypeSupported("audio/webm; codecs=opus")) {
          mimeType = "audio/webm; codecs=opus";
        }

        mediaRecorderRef.current = new MediaRecorder(
          stream,
          mimeType ? { mimeType } : {},
        );
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: mimeType || "audio/webm",
          });
          setServiceTicketDetails((prev) => ({
            ...prev,
            serviceAudioDescription: audioBlob,
          }));

          clearInterval(recordTimerRef.current);
          setRecordTime(0);
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);

        recordTimerRef.current = setInterval(() => {
          setRecordTime((prev) => prev + 1);
        }, 1000);
      } catch (err) {
        console.error("Microphone error:", err);
      }
    } else {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordTimerRef.current);
    }
  };

  useEffect(() => {
    if (serviceTicketDetails?.serviceAudioDescription) {
      const url = URL.createObjectURL(
        serviceTicketDetails.serviceAudioDescription,
      );
      setAudioURL(url);

      // Cleanup when the blob changes or component unmounts
      return () => {
        URL.revokeObjectURL(url);
        setAudioURL("");
      };
    }
  }, [serviceTicketDetails.serviceAudioDescription]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioURL) return;

    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleTimeUpdate = () => setProgress(audio.currentTime);

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioURL]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.error("Audio playback failed:", err));
    }
  };

  const handleSliderChange = (_, value) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = value;
    setProgress(value);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(Math.floor(seconds % 60)).padStart(2, "0");
    return `${m}:${s}`;
  };
  // --------------------------------------------------------------

  // ------------------ FOR IMAGE UPLOAD FEATURE ------------------
  // Allowing zoom of an image
  const [isZoomed, setIsZoomed] = useState(false);

  // Reset zoom when overlayFile changes
  useEffect(() => {
    setIsZoomed(false);
  }, [overlayFile]);

  // For pasting an image
  useEffect(() => {
    const handlePaste = (event) => {
      const items = event.clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          const blob = item.getAsFile();

          const file = new File([blob], `pasted-image-${Date.now()}.png`, {
            type: blob.type,
          });

          const fileWithPreview = Object.assign(file, {
            preview: URL.createObjectURL(file),
          });

          setServiceTicketDetails((prev) => ({
            ...prev,
            serviceImages: [...prev.serviceImages, fileWithPreview].slice(0, 5),
          }));

          setSnackBarConfig({
            open: true,
            message: "Image pasted successfully",
            severity: "success",
          });

          break; // Only handle first image
        }
      }
    };

    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, []);

  // Dropzone logic
  const onDrop = (acceptedFiles) => {
    const validFiles = [];
    const rejectedFiles = [];

    acceptedFiles.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        rejectedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    if (rejectedFiles.length > 0) {
      setSnackBarConfig({
        open: true,
        message: "Each file must be less than or equal to 5 MB",
        severity: "error",
      });
    }

    if (validFiles.length > 0) {
      const newFiles = validFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) }),
      );

      setServiceTicketDetails((prev) => ({
        ...prev,
        serviceImages: [...prev.serviceImages, ...newFiles].slice(0, 5), // max 5 uploads
      }));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "video/mp4": [],
      "video/x-matroska": [],
      "application/pdf": [],
      "application/msword": [],
    },
    multiple: true,
    disabled: serviceTicketDetails.serviceImages.length >= 5, // 👈 disable after 5 uploads
  });

  // Delete an image after uploading
  const handleDeleteImage = (index) => {
    setServiceTicketDetails((prev) => ({
      ...prev,
      serviceImages: prev.serviceImages.filter((_, i) => i !== index),
    }));
  };

  // Handle clicking a file to open dialog
  const handleFileClick = (file) => {
    setOverlayFile(file);
  };
  // --------------------------------------------------------------

  // Snackbar config
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Close Snackbar
  const handleClose = () => {
    setSnackBarConfig((prevState) => ({ ...prevState, open: false }));
  };

  // Date formatter (for input type="date")
  function formatDate(date) {
    if (!date) return "";

    // If string already in yyyy-MM-dd format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // ✅ no spaces
  }

  // Form field values change handler
  const handleChange = (targetField, targetValue) => {
    if (targetField === "serviceDescription") {
      setServiceDescriptionCharCount(targetValue.length);
    }

    setServiceTicketDetails((prevState) => {
      // If start date changes, shift deadline to +1 day
      if (targetField === "serviceStartDate") {
        const newStart = new Date(targetValue);
        const newDeadline = new Date(newStart);
        newDeadline.setDate(newStart.getDate() + 1);

        return {
          ...prevState,
          serviceStartDate: formatDate(newStart),
          serviceDeadline: formatDate(newDeadline),
        };
      }

      return {
        ...prevState,
        [targetField]: targetValue,
      };
    });
  };

  // Assign now form field values change handler
  const handleAssignForm = (field) => (event) => {
    setAssignForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // Check for non-empty fields
  const validateFields = () => {
    if (!serviceTicketDetails.serviceDescription.trim())
      return "Description is required";
    if (!serviceTicketDetails.serviceDeadline)
      return "Please select a Service Delivery Date";
    if (!serviceTicketDetails.servicePriorityLevel)
      return "Please select the priority";
    if (
      serviceTicketDetails.serviceIsRecurring &&
      !serviceTicketDetails.serviceFrequencyValue
    ) {
      return "Please select the frequency";
    }

    return null;
  };

  // Raise ticket handler
  const handleSubmit = async () => {
    const errorMessage = validateFields();
    if (errorMessage) {
      setSnackBarConfig({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      return;
    }
    if (
      serviceTicketDetails.serviceDescription.length >
      serviceDescriptionTotalChar
    ) {
      setSnackBarConfig({
        open: true,
        message: "Description too long",
        severity: "error",
      });
      return;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/createticket`;
    setLoading(true);

    try {
      const formData = new FormData();
      let type;
      if (userStoreID === 0)
        type = storeForm.store_id !== "" ? "external" : "internal";
      if (userStoreID !== 0)
        type = userStoreID === selectedDeptStoreId ? "internal" : "external";
      formData.append("type", type);

      const ticketStoreId =
        storeForm.store_id !== "" ? storeForm.store_id : userStoreID;
      formData.append("store_id", ticketStoreId);

      // The branch this ticket was RAISED at — attribution, not routing. It is
      // sent only when the ticket belongs to the store this session is in.
      //
      // That condition is the whole point: raising a ticket FOR another store
      // (Kumudu filing one against a customer's shop) sends that store's id,
      // and our branch is not one of its branches. The server is strict about a
      // branch it was actually given — stamping a row with another store's
      // branch would corrupt every report built on it — so it would reject the
      // save outright. Sending nothing instead lets it stamp that store's main
      // branch, which is the honest answer: we do not know which of their
      // counters a ticket we raised on their behalf belongs to.
      const ownStoreId = currentStoreInfo?.id ?? userStoreID;
      if (
        branchScope?.mode === "branch" &&
        branchScope?.branchId != null &&
        Number(ticketStoreId) === Number(ownStoreId)
      ) {
        formData.append("branch_id", branchScope.branchId);
      }
      formData.append("dept_id", dept_id);
      storeForm.store_id === ""
        ? formData.append("createdby", userInfo.id)
        : storeForm.head_id !== null
        ? formData.append("createdby", storeForm.head_id)
        : formData.append("createdby", 1);
      formData.append("description", serviceTicketDetails?.serviceDescription);
      formData.append("priority", serviceTicketDetails?.servicePriorityLevel);
      formData.append("deadline", serviceTicketDetails?.serviceDeadline);
      formData.append("start_date", serviceTicketDetails?.serviceStartDate);
      formData.append(
        "status",
        assignForm.status === "recurring" ? "recurring" : "",
      );
      formData.append("value", serviceTicketDetails?.serviceFrequencyValue);
      formData.append(
        "frequency",
        serviceTicketDetails?.ServiceTicketFrequency,
      );
      formData.append(
        "pm_id",
        isDialogMode && pmTask?.pm_id ? pmTask.pm_id : "",
      );
      formData.append(
        "is_follow_up",
        assignForm.status === "follow_up" ? "true" : "false",
      );

      // AUDIO RECORD
      if (serviceTicketDetails.serviceAudioDescription)
        formData.append(
          "media",
          serviceTicketDetails?.serviceAudioDescription,
          "recording.webm",
        );

      // Multiple files
      serviceTicketDetails.serviceImages.forEach((file) =>
        formData.append("media", file),
      );

      const res = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (!res?.data?.success) throw res?.data;

      const createdTicketInfo = res?.data?.data;
      setTicket(createdTicketInfo);

      // Assign Ticket (if required)
      if (isAssignNow && assignForm.assigned_to !== "") {
        const assignTicketUrl = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/assignticket`;

        const assignFormData = new FormData();
        assignFormData.append("ticket_id", createdTicketInfo.id);
        assignFormData.append("assigned_to", assignForm.assigned_to);
        assignFormData.append("description", assignForm.description);
        assignFormData.append("time_budget", assignForm.time_budget);
        assignFormData.append(
          "time_taken",
          assignForm.status === "resolved" ? assignForm.time_budget : "",
        );
        assignFormData.append("status", assignForm.status);
        assignFormData.append("assigned_from", user_id);

        // Append the same media files as in ticket creation
        serviceTicketDetails.serviceImages.forEach((file) =>
          assignFormData.append("media", file),
        );

        // Also include audio if present
        if (serviceTicketDetails.serviceAudioDescription)
          assignFormData.append(
            "media",
            serviceTicketDetails.serviceAudioDescription,
            "recording.webm",
          );

        try {
          const assignResponse = await axios.post(
            assignTicketUrl,
            assignFormData,
            { headers: { "Content-Type": "multipart/form-data" } },
          );

          if (assignResponse?.data?.success) {
            setAssignRes(assignResponse.data.data);

            // The backend sends the store group its "Ticket Resolved" message
            // and reports here only when that failed.
            const whatsAppWarning = whatsAppFailureText(
              assignResponse.data.whatsapp,
              "Ticket created",
            );
            if (whatsAppWarning) {
              setSnackBarConfig({
                open: true,
                message: whatsAppWarning,
                severity: "warning",
              });
            }
          } else {
            throw new Error(
              assignResponse?.data?.statusMessage || "Failed to assign ticket",
            );
          }
        } catch (assignErr) {
          setSnackBarConfig({
            open: true,
            message:
              assignErr.message || "Ticket was created but assignment failed",
            severity: "error",
          });
        }
      }

      setLoading(false);
      if (isDialogMode && onTicketCreated) {
        onTicketCreated({
          ticket_id: createdTicketInfo.id,
          ticket_number: createdTicketInfo.ticket_number,
        });
      } else {
        setOverlayOpen(true);
      }
      setServiceDescriptionCharCount(0);

      // Reset form
      setServiceTicketDetails({
        serviceDescription: "",
        serviceAudioDescription: "",
        serviceImages: [],
        serviceDeadline: "",
        servicePriorityLevel: "",
      });
    } catch (err) {
      setLoading(false);
      setSnackBarConfig({
        open: true,
        message: err.statusMessage || "Ticket was not raised",
        severity: "error",
      });
    }
  };

  // Show pending tickets of the selected store
  useEffect(() => {
    const handleFetchTickets = async () => {
      // No store, no request.
      //
      // storeForm.store_id starts as "", and this effect runs on mount, so the
      // id interpolated into the path was empty and the URL collapsed to
      // ".../getTicketsByStore/" — a 404 on every mount and again every time
      // the store is cleared. The catch swallowed it, so it only ever showed up
      // as console noise, but the request was real and pointless.
      //
      // Nothing is reset here on purpose: selectedStorePendingTickets already
      // refuses to render unless store_id is set, so leaving the previous
      // store's tickets in state is invisible and avoids a needless re-render.
      if (!storeForm?.store_id && storeForm?.store_id !== 0) return;

      try {
        let url;
        url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getTicketsByStore/${storeForm?.store_id}`;

        const res = await axios.get(url);
        const allTickets = res?.data?.data || [];

        // Set all the tickets
        setAllTickets(allTickets);

        // Filter all tickets as per department
        // const allTicketsPerDepartment =
        //   allTickets.filter((ticket) => ticket.dept_id === userInfo.dept_id) ||
        //   [];
        // userInfo.role === "storehead" || userInfo.role === "servicehead"
        //   ? setAllTickets(allTickets)
        //   : setAllTickets(allTicketsPerDepartment);
      } catch (error) {
        console.error("API error:", error);
      }
    };

    handleFetchTickets();
    // eslint-disable-next-line
  }, [storeForm?.store_id]);

  const selectedStorePendingTickets = [];
  if (storeForm.store_id !== "" && allTickets !== null) {
    selectedStorePendingTickets.push(
      ...allTickets?.filter(
        (ticket) =>
          ticket.store_id === storeForm.store_id &&
          ticket.status === "in_progress",
      ),
    );
  }

  return (
    <>
      {/* The Form Container */}
      <Box
        sx={
          isDialogMode
            ? {
                width: "100%",
                bgcolor: "white",
                px: 2,
                pb: 2,
              }
            : {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                m: "auto",
                mt: 2.5,
                mb: -1,
                py: 1.5,
                bgcolor: "white",
                position: "relative",
                boxShadow: "0 5px 15px rgba(75, 75, 75, 0.15)",
                background: "linear-gradient(180deg, #FFFFFF 0%, #EFFAFA 100%)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -12,
                  left: 0,
                  width: "100%",
                  height: 50,
                  backgroundImage:
                    "radial-gradient(circle at 10px 10px, white 10px, transparent 0px)",
                  backgroundSize: "19px 19px",
                  backgroundRepeat: "repeat-x",
                },
              }
        }
      >
        {/* The Form */}
        <Box sx={{ width: "95%" }}>
          {/* Raise On Store's Behalf */}
          {userStoreID === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", mb: -1 }}>
              <Autocomplete
                fullWidth
                options={selectableStores}
                getOptionLabel={(option) =>
                  option?.store_name
                    ? `${option.store_id} - ${option.store_name}`
                    : ""
                }
                value={
                  selectableStores.find(
                    (store) => store.store_name === storeForm.store_name,
                  ) || null
                }
                onChange={(event, newValue) => {
                  setStoreForm({
                    store_name: newValue ? newValue.store_name : "",
                    store_id: newValue ? newValue.store_id : null,
                    head_id: newValue ? newValue.head_id : null,
                  });
                }}
                filterOptions={(options, { inputValue }) => {
                  const lowerInput = inputValue.trim().toLowerCase();

                  return options.filter(
                    (option) =>
                      String(option?.store_name ?? "")
                        .toLowerCase()
                        .includes(lowerInput) ||
                      String(option?.store_id ?? "") === lowerInput,
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    sx={formStyle}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment:
                        !isFocused && !storeForm.store_name ? (
                          <InputAdornment
                            position="start"
                            sx={{ gap: 1, color: "text.secondary" }}
                          >
                            <StoreRoundedIcon sx={{ fontSize: 20 }} />
                            <span>Select Store</span>
                          </InputAdornment>
                        ) : null,
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* Service Description */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <TextField
              multiline
              minRows={3}
              label={
                isDescriptionFocused || serviceTicketDetails.serviceDescription
                  ? ""
                  : "Description"
              }
              onFocus={() => setIsDescriptionFocused(true)}
              onBlur={() => setIsDescriptionFocused(false)}
              variant="outlined"
              fullWidth
              value={serviceTicketDetails.serviceDescription}
              onChange={(e) =>
                handleChange("serviceDescription", e.target.value)
              }
              inputProps={{
                // maxLength: serviceDescriptionTotalChar,
                inputMode: "text",
              }}
              sx={{
                "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
                "& .MuiOutlinedInput-input": { fontSize: 14, lineHeight: 1.5 },
                "& .MuiOutlinedInput-inputMultiline": { padding: "8px 10px" },
              }}
            />
            <Typography sx={{ fontSize: 10, alignSelf: "flex-end" }}>
              <span
                style={{
                  color:
                    serviceDescriptionCharCount <= serviceDescriptionTotalChar
                      ? "rgba(0, 200, 0, 1)"
                      : "rgba(200, 0, 0, 1)",
                }}
              >
                {serviceDescriptionCharCount}/{serviceDescriptionTotalChar}
              </span>{" "}
              characters
            </Typography>
          </Box>

          {/* Attachments */}
          <Box sx={{ display: "flex", flexDirection: "column", mb: 1 }}>
            {/* Container for Dropzone + Previews */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(4, 1fr)",
                  sm: "repeat(6, 1fr)",
                  md: "repeat(6, 1fr)",
                },
                gap: 1,
              }}
            >
              {/* Dropzone */}
              <Box
                {...getRootProps()}
                sx={{
                  aspectRatio: "1 / 1",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  cursor:
                    serviceTicketDetails.serviceImages.length >= 5
                      ? "not-allowed"
                      : "pointer",
                  bgcolor:
                    serviceTicketDetails.serviceImages.length >= 5
                      ? theme.lightTheme.themeCol
                      : "white",
                  border:
                    serviceTicketDetails.serviceImages.length >= 5
                      ? "1px solid #b30000ff"
                      : "2px dashed #aaa",
                  transition: "all 0.3s ease-in-out",

                  // NEW: enforce consistent size
                  width: "100%",
                  maxWidth: 90, // adjust tile size (try 80–100)
                  minWidth: 70,
                  mx: "auto",
                }}
              >
                <input
                  {...getInputProps()}
                  disabled={serviceTicketDetails.serviceImages.length >= 5}
                />

                <CloseRoundedIcon
                  sx={{
                    fontSize: 28,
                    color:
                      serviceTicketDetails.serviceImages.length >= 5
                        ? "#ff6a6aff"
                        : theme.lightTheme.placeholder,
                    transform:
                      serviceTicketDetails.serviceImages.length >= 5
                        ? "rotate(0deg)"
                        : "rotate(-135deg)",
                    transition: "transform 0.3s ease-in-out",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    textAlign: "center",
                    mt: 0.5,
                    color:
                      serviceTicketDetails.serviceImages.length >= 5
                        ? "white"
                        : theme.lightTheme.placeholder,
                  }}
                >
                  {serviceTicketDetails.serviceImages.length >= 5
                    ? "Max 5"
                    : "Upload"}
                </Typography>
              </Box>

              {/* File Previews */}
              {serviceTicketDetails?.serviceImages?.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    aspectRatio: "1 / 1",
                    borderRadius: 2,
                    position: "relative",
                    overflow: "hidden",
                    bgcolor: "#f5f5f5",
                    cursor: "pointer",

                    // match dropzone sizing
                    width: "100%",
                    maxWidth: 90,
                    minWidth: 70,
                    mx: "auto",
                  }}
                  onClick={() => handleFileClick(file)}
                >
                  {/* Preview Layer */}
                  {file.type.includes("image") ? (
                    <Box
                      component="img"
                      src={file.preview}
                      alt="preview"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : file.type.includes("video") ? (
                    <Box
                      component="video"
                      src={file.preview}
                      muted
                      autoPlay
                      loop
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : file.type.includes("pdf") ? (
                    <PictureAsPdfRoundedIcon
                      sx={{
                        fontSize: 48,
                        color: theme.lightTheme.themeCol,
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  ) : (
                    <InsertDriveFileRoundedIcon
                      sx={{
                        fontSize: 48,
                        color: "#666",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  )}

                  {/* Delete Button - Always on Top */}
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(index);
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: { xs: 24, sm: 30, md: 36 },
                      height: { xs: 24, sm: 30, md: 36 },
                      borderRadius: "50%",
                      bgcolor: "rgba(0,0,0,0.7)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: { xs: 14, sm: 16, md: 18 },
                      fontWeight: "bold",
                      cursor: "pointer",
                      zIndex: 2,
                    }}
                  >
                    ✕
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Show file in full window view */}
            <Dialog
              open={!!overlayFile}
              onClose={() => setOverlayFile(null)}
              fullWidth
              BackdropProps={{
                sx: {
                  backgroundColor: "rgba(0,0,0,0.5)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  transition: "all 0.4s ease-in-out",
                },
              }}
              TransitionProps={{
                timeout: 400, // sync with backdrop transition
              }}
            >
              {overlayFile && (
                <Box sx={{ textAlign: "center" }}>
                  {/* Images */}
                  {overlayFile.type.startsWith("image/") && (
                    <Box
                      sx={{
                        maxWidth: "100%",
                        maxHeight: { xs: "65vh", sm: "80vh", md: "80vh" },
                        overflow: "hidden",
                      }}
                    >
                      <TransformWrapper
                        initialScale={1}
                        minScale={1}
                        maxScale={5}
                        wheel={{ step: 0.1 }}
                        pinch={{ step: 5 }}
                        doubleClick={{ disabled: false }}
                      >
                        {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                          <>
                            <TransformComponent>
                              <img
                                src={
                                  overlayFile.preview ||
                                  URL.createObjectURL(overlayFile.file)
                                }
                                alt={overlayFile.name}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "80vh",
                                  objectFit: "contain",
                                  cursor: "grab",
                                }}
                              />
                            </TransformComponent>
                          </>
                        )}
                      </TransformWrapper>
                    </Box>
                  )}

                  {/* Videos */}
                  {overlayFile.type.startsWith("video/") && (
                    <Box
                      sx={{
                        maxWidth: "100%",
                        maxHeight: { xs: "65vh", sm: "80vh", md: "80vh" },
                      }}
                    >
                      <video
                        controls
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                      >
                        <source
                          src={overlayFile.preview}
                          type={overlayFile.type}
                        />
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                  )}

                  {/* PDFs */}
                  {overlayFile.type === "application/pdf" && (
                    <Box
                      sx={{
                        width: "100%",
                        height: { sm: "80vh", md: "80vh" }, // ✅ force height
                      }}
                    >
                      <object
                        data={overlayFile.preview}
                        type="application/pdf"
                        width="100%"
                        height="100%" // ✅ object fills container
                      >
                        <Typography sx={{ fontSize: 16 }}>
                          PDF preview is not supported.
                        </Typography>
                      </object>
                    </Box>
                  )}
                </Box>
              )}
            </Dialog>

            {/* Audio */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: `1px solid ${theme.lightTheme.placeholder}`,
                borderRadius: 3,
                px: 1,
                py: 0.5,
                mt: 1,
                bgcolor: "#FFF",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "100%",
                  gap: 1,
                  px: 1.25,
                  py: 1,
                }}
              >
                {/* Mic Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 56,
                    minHeight: 56,
                    backgroundColor: isRecording
                      ? "#003f3fff"
                      : theme.lightTheme.themeCol,
                    boxShadow: isRecording
                      ? "inset 0px 0px 10px #000000ff"
                      : `inset 0px 0px 15px ${theme.lightTheme.themeCol}`,
                    borderRadius: 100,
                    transition:
                      "background-color 0.4s ease, box-shadow 0.4s ease",
                  }}
                >
                  <MicNoneOutlinedIcon
                    onClick={handleMicClick}
                    sx={{
                      fontSize: 36,
                      color: isRecording ? "#00a2ffff" : "white",
                      cursor: "pointer",
                    }}
                  />
                </Box>

                {/* Record Indicator Bars & Timer */}
                {isRecording && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      gap: 1,
                      flex: 1,
                      p: 1,
                      borderRadius: 10,
                      bgcolor: "#F8FFFF",
                      border: "1px solid #EEEEEE",
                      minHeight: 65,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 24,
                        gap: "2px",
                      }}
                    >
                      {[...Array(25)].map((_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 2.5,
                            height: 10,
                            bgcolor: theme.lightTheme.themeCol,
                            borderRadius: 100,
                            animation: `wave 1s ease-in-out infinite`,
                            animationDelay: `${i * 0.15}s`,
                          }}
                        />
                      ))}
                    </Box>
                    <Typography
                      sx={{
                        color: theme.lightTheme.themeCol,
                        fontWeight: 600,
                        minWidth: 60,
                        fontSize: 18,
                      }}
                    >
                      {formatTime(recordTime)}
                    </Typography>

                    <style>
                      {`@keyframes wave {0%, 100% { transform: scaleY(0.4); }50% { transform: scaleY(1.2); }}`}
                    </style>
                  </Box>
                )}

                {/* Custom Audio Player */}
                {audioURL && !isRecording && (
                  <Box
                    sx={{
                      flex: 1,
                      p: 1,
                      borderRadius: 10,
                      bgcolor: "#F8FFFF",
                      border: "1px solid #EEEEEE",
                      minHeight: 65,
                    }}
                  >
                    <audio ref={audioRef} src={audioURL} preload="metadata" />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <IconButton onClick={togglePlay}>
                        {isPlaying ? (
                          <PauseRoundedIcon />
                        ) : (
                          <PlayArrowRoundedIcon />
                        )}
                      </IconButton>

                      <Slider
                        min={0}
                        max={duration}
                        value={progress}
                        onChange={handleSliderChange}
                        sx={{
                          flex: 1,
                          color: theme.button.primaryBtn,
                          "& .MuiSlider-thumb": {
                            backgroundColor: theme.button.primaryBtn,
                          },
                        }}
                      />

                      <IconButton onClick={toggleMute}>
                        {isMuted ? (
                          <VolumeOffRoundedIcon />
                        ) : (
                          <VolumeUpRoundedIcon />
                        )}
                      </IconButton>

                      <Typography variant="caption">
                        {/* {formatTime(progress)} / {formatTime(duration)} */}
                        {formatTime(duration)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Start and End (Deadline) Date */}
          {userStoreID === selectedDeptStoreId && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",

                width: "100%",
                gap: 1.5,
              }}
            >
              {/* Start Date */}
              <TextField
                type="date"
                value={serviceTicketDetails.serviceStartDate}
                onChange={(e) =>
                  handleChange("serviceStartDate", e.target.value)
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& input": {
                    pl: 4,
                  },
                  "& input::-webkit-calendar-picker-indicator": {
                    position: "absolute",
                    left: 8,
                    cursor: "pointer",
                    filter: "invert(0.5)",
                  },
                }}
              />

              {/* End Date (Deadline) */}
              <TextField
                type="date"
                value={serviceTicketDetails.serviceDeadline}
                onChange={(e) =>
                  handleChange("serviceDeadline", e.target.value)
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& input": {
                    pl: 4,
                  },
                  "& input::-webkit-calendar-picker-indicator": {
                    position: "absolute",
                    left: 8,
                    cursor: "pointer",
                    filter: "invert(0.5)",
                  },
                }}
              />
            </Box>
          )}

          {/* Service Priority and Time Budget */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",

              width: "100%",
              gap: 1.5,
            }}
          >
            {/* Priority */}
            <Autocomplete
              options={priorityOptions}
              getOptionLabel={(option) => option.label}
              value={
                priorityOptions.find(
                  (opt) =>
                    opt.value === serviceTicketDetails.servicePriorityLevel,
                ) || priorityOptions[1]
              }
              onChange={(_, newValue) => {
                if (newValue) {
                  handleChange("servicePriorityLevel", newValue.value);
                }
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  {option.icon}
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#FFFCF7",
                      borderRadius: "6px",
                      color: getPriorityColor(
                        serviceTicketDetails.servicePriorityLevel,
                      ),
                      "& fieldset": {
                        borderColor: getPriorityColor(
                          serviceTicketDetails.servicePriorityLevel,
                        ),
                      },
                      "&:hover fieldset": {
                        borderColor: getPriorityColor(
                          serviceTicketDetails.servicePriorityLevel,
                        ),
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: getPriorityColor(
                          serviceTicketDetails.servicePriorityLevel,
                        ),
                      },
                    },
                  }}
                />
              )}
              sx={{ flex: 1 }}
            />

            {/* Time Budget */}
            {userStoreID === selectedDeptStoreId && (
              <TextField
                type="number"
                size="small"
                variant="outlined"
                value={assignForm.time_budget}
                onChange={handleAssignForm("time_budget")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: "12px" }}>mins</Typography>
                      <HistoryToggleOffRoundedIcon
                        sx={{ fontSize: "20px", ml: 0.5 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, ...formStyle }}
              />
            )}
          </Box>

          {/* Assign Now and Status */}
          {userStoreID === selectedDeptStoreId && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",

                width: "100%",
                gap: 1.5,
              }}
            >
              {/* Select team member */}
              <Autocomplete
                options={allStaff}
                getOptionLabel={(option) => option.name || ""}
                value={
                  allStaff.find(
                    (staff) => staff.id === assignForm.assigned_to,
                  ) || null
                }
                disabled={assignForm.status === "follow_up"}
                onChange={(event, newValue) => {
                  setAssignForm((prev) => ({
                    ...prev,
                    assigned_to: newValue ? newValue.id : "",
                  }));
                }}
                filterOptions={(options, state) => {
                  const searchValue = state.inputValue.toLowerCase();
                  return options.filter((option) =>
                    option.name?.toLowerCase().includes(searchValue),
                  );
                }}
                ListboxProps={{
                  style: { maxHeight: 200 },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    sx={formStyle}
                  />
                )}
                sx={{ width: "100%" }}
              />

              {/* Ticket Statuses */}
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: getStatusBorderColor(assignForm.status),
                    },
                    "&:hover fieldset": {
                      borderColor: getStatusBorderColor(assignForm.status),
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: getStatusBorderColor(assignForm.status),
                    },
                  },
                  "& .MuiSelect-select": {
                    color: getStatusColor(assignForm.status),
                  },
                }}
              >
                <Select
                  value={assignForm.status}
                  onChange={handleAssignForm("status")}
                >
                  <MenuItem
                    value="in_progress"
                    sx={{
                      color: getStatusColor("in_progress"),
                      bgcolor: getStatusBgColor("in_progress"),
                    }}
                  >
                    In Progress
                  </MenuItem>
                  {isServiceHead(userInfo.role) && (
                    <MenuItem
                      value="recurring"
                      sx={{
                        color: getStatusColor("recurring"),
                        bgcolor: getStatusBgColor("recurring"),
                      }}
                    >
                      Recurring
                    </MenuItem>
                  )}
                  <MenuItem
                    value="resolved"
                    sx={{
                      color: getStatusColor("resolved"),
                      bgcolor: getStatusBgColor("resolved"),
                    }}
                  >
                    Resolved
                  </MenuItem>
                  <MenuItem
                    value="follow_up"
                    sx={{
                      color: getStatusColor("follow_up"),
                      bgcolor: getStatusBgColor("follow_up"),
                    }}
                  >
                    Follow Up
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Recurring Task Frequencies */}
          <Collapse in={assignForm.status === "recurring"} timeout={300}>
            <Grid container spacing={2}>
              {/* Frequency Dropdown — always 50% width */}
              <Grid
                item
                sx={{
                  flex: "1 1 50%",
                  transition: "all 0.3s ease",
                  maxWidth:
                    serviceTicketDetails.ServiceTicketFrequency === "daily"
                      ? "100%"
                      : "50%",
                }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{ ...formStyle, padding: 0 }}
                >
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={serviceTicketDetails.ServiceTicketFrequency}
                    onChange={(e) => {
                      const freq = e.target.value;
                      handleChange("ServiceTicketFrequency", freq);
                      handleChange(
                        "serviceFrequencyValue",
                        freq === "daily" ? "daily" : "",
                      );
                    }}
                    label="Frequency"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Day / Date Dropdown — always present, inner content collapsed */}
              <Grid
                item
                sx={{
                  flex: "1 1 50%",
                  transition: "all 0.3s ease",
                  maxWidth:
                    serviceTicketDetails.ServiceTicketFrequency === "daily"
                      ? 0
                      : "50%",
                  overflow: "hidden", // ensures hidden content doesn't take space
                }}
              >
                <Collapse
                  in={serviceTicketDetails.ServiceTicketFrequency !== "daily"}
                  timeout={300}
                >
                  <FormControl
                    required
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ ...formStyle, padding: 0 }}
                  >
                    <InputLabel>
                      {serviceTicketDetails.ServiceTicketFrequency === "weekly"
                        ? "Day"
                        : "Date"}
                    </InputLabel>
                    <Select
                      size="small"
                      value={serviceTicketDetails.serviceFrequencyValue}
                      onChange={(e) =>
                        handleChange("serviceFrequencyValue", e.target.value)
                      }
                      label={
                        serviceTicketDetails.ServiceTicketFrequency === "weekly"
                          ? "Day"
                          : "Date"
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300, // dropdown max height in pixels
                            overflowY: "auto", // scrollable if content exceeds maxHeight
                          },
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null, // ensures proper alignment
                      }}
                    >
                      {serviceTicketDetails.ServiceTicketFrequency === "weekly"
                        ? ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(
                            (d) => (
                              <MenuItem key={d} value={d}>
                                {d.charAt(0).toUpperCase() + d.slice(1)}
                              </MenuItem>
                            ),
                          )
                        : [...Array(28)].map((_, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                              {i + 1}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Collapse>
              </Grid>
            </Grid>
          </Collapse>

          {/* Raise Ticket Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 2.5,
              mb: 5.5,
              width: "100%",
            }}
          >
            <Button
              sx={{
                background: "linear-gradient(90deg, #008080 0%, #4428A0 100%)",
                color: "white",
                width: "100%",
                fontSize: 16,
                boxShadow: `
                  inset -2px -2px 12px rgba(0,0,0,0.5),  
                  0px 4px 20px rgba(0,0,0,0.4)           
                `,
                borderRadius: 2.5,
                py: 1.25,
              }}
              onClick={guardBranch(handleSubmit)}
            >
              Raise Ticket
            </Button>
          </Box>
        </Box>

        {/* Below Notch */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0.25,
            left: "50%",
            transform: "translateX(-50%)",
            width: 80,
            height: 40,
            borderBottom: "none",
            borderTopLeftRadius: "100px",
            borderTopRightRadius: "100px",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: "#f4f6f8",
            mx: "auto",
            boxShadow: "inset 0px 5px 10px rgba(0, 0, 0, 0.15)",
          }}
        />
      </Box>

      {/* Already Pending Tickets for Selected Store */}
      {selectedStorePendingTickets.length > 0 && (
        <>
          <Typography sx={{ fontSize: 18, fontWeight: 600, mt: 4 }}>
            Already Pending Tickets
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              width: "100%",
              mt: 1,
              mb: 5,
            }}
          >
            {selectedStorePendingTickets.map((ticket, index) => (
              <TicketCard
                key={index}
                currentView="list"
                filteredTickets={selectedStorePendingTickets}
                index={index}
                ticket={ticket}
                storeName={storeForm?.store_name}
              />
            ))}
          </Box>
        </>
      )}

      {/* Loader Overlay */}
      {loading && <LoadingScreen loadingMessage={"Generating ticket"} />}

      {/* Show Created Ticket as Overlay */}
      {overlayOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(8px)",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            zIndex: isMobile ? 999 : 99999,
          }}
        >
          <TicketReceipt
            ticketInfo={ticket || ""}
            assignInfo={assignRes || ""}
          />
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important", // force override snackbar position
          },
        }}
      >
        {/* You can use Alert for styled messages */}
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>

      {/* Opens only if Raise Ticket was pressed while every branch was on
          screen. Renders nothing otherwise. */}
      {branchGuardDialog}
    </>
  );
}

const formStyle = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",

    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.borders.secondaryBorder,
    fontSize: "14px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.borders.secondaryBorder,
  },
};

export default RaiseServiceTicket;
